/**
 * UserSettingsPosHardware — Pantalla «POS, gaveta y seguridad» (térmica + tope de efectivo).
 *
 * QUÉ HACE:
 *  - Permite configurar la impresora térmica POS (papel 58/80 mm, marca, transporte
 *    Wi‑Fi/Bluetooth/USB) y las preferencias de comportamiento (impresión de
 *    recibos, impresión automática y apertura automática de gaveta).
 *  - Orden del panel: primero lo principal (nombre, papel, marca, tipo y pruebas de
 *    hardware), luego «Opciones avanzadas» (incluye Pedidos de tienda / auto-impresión)
 *    y el banner web de vías de impresión queda hasta abajo, antes del pie de acciones.
 *  - Incluye reglas globales de efectivo en gaveta (multicaja) y, aparte, el PIN cada N ventas,
 *    persistidos vía `cashDrawerSecurityConfig` — solo debe montarse para el dueño del negocio.
 *  - Persiste impresora vía `posPrinterConfig` (LS + SharedPreferences en APK).
 *  - Pruebas: imprimir página de prueba y abrir gaveta una sola vez (anti-fraude).
 *
 * QUÉ EXPORTA: UserSettingsPosHardware (named).
 *
 * QUÉ NO DEBE HACER:
 *  - No procesa ventas (la evaluación en cobro vive en PaymentView + policy).
 *  - No define componentes UI propios: REUTILIZA `FloatingLabelInput`, `MenuSelect`,
 *    `Switch/SwitchRow`, `PrinterPaperSizeSelector` y las secciones extraídas
 *    (`NetworkPrinterFields`, `BluetoothPrinterFields`, `UsbPrinterFields`,
 *    `LabelPrinterFields`, `PosHardwareTestsSection`, `PosWebPrintBanner`).
 *    Cualquier nuevo widget que necesite reutilizarse debe vivir en components/ui
 *    o en el feature.
 *
 * REGLAS:
 *  - `mode="full"` (dueño/admin): panel completo (impresora + pruebas + opciones
 *    avanzadas: pedidos de tienda, métodos de pago, tope de efectivo, recibos).
 *    Es el modo del slider de perfil y del engranaje ⚙ de caja para dueño/admin.
 *  - `mode="cashier"` (empleado con acceso a caja): TODA la conexión de la térmica —
 *    nombre de impresora, tamaño de papel, marca, tipo y campos de conexión
 *    (red/relay, Bluetooth, USB) + pruebas de hardware — para que el cajero pueda
 *    reconectar la impresora si pasa algo. Lo que NO ve: «Opciones avanzadas»
 *    (globales del negocio: pedidos de tienda, métodos de pago, tope de efectivo,
 *    identificación, PIN admin, recibos) ni el borrado de configuración.
 *  - El botón «Probar gaveta» queda BLOQUEADO tras una prueba exitosa
 *    (drawerTestUsed). Sólo el reset total libera la prueba: protege al dueño.
 *  - Los selects de marca/tipo NO usan `<select>` nativo (UX móvil) → MenuSelect.
 *  - El nombre de la impresora va arriba con etiqueta flotante (FloatingLabelInput),
 *    consistente con el resto de formularios del proyecto.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Printer,
  Wifi,
  ChevronDown,
  Trash2,
  Cpu,
  Loader2,
  AlertTriangle,
  Percent,
} from "lucide-react";
import toast from "react-hot-toast";
import { isNative, isDesktop } from "@/lib/platform";
import { AltenotPos } from "@/lib/native/altenotPos";
import { tryPrintThermalTestPage } from "@/lib/native/posHardware";
import { openCashDrawer } from "@/lib/native/posHardware";
import { tryPrintThermalWeighLabel } from "@/lib/native/posHardware";
import {
  encodeWeighLabelCode128,
  encodeWeighLabelQr,
} from "@/lib/weighBarcode";
import {
  type PosPrinterConfig,
  type PosPrinterTransport,
  type LabelPrinterMode,
  DEFAULT_POS_PRINTER_CONFIG,
  POS_DEFAULT_NETWORK_HOST,
  loadPosPrinterConfig,
  persistPosPrinterConfig,
  isPosPrinterConfigured,
  isLabelPrinterConfigured,
  isDrawerTestAllowed,
  markDrawerTestUsed,
  resetPosPrinterConfig,
} from "@/lib/native/posPrinterConfig";
import { POS_CONNECTION_TYPE_LABELS } from "@/features/caja/constants/posPrinterCatalog";
import { businessConfigService } from "@/features/caja/services/businessConfig.service";
import type {
  StoredBusinessConfig,
  TicketFormato,
} from "@/features/caja/utils/businessConfigStorage";
import { ConfirmationModal } from "@/components/common/ConfirmationModal";
import { FloatingLabelInput } from "@/components/ui/FloatingLabelInput";
import { MenuSelect } from "@/components/ui/MenuSelect";
import { PrinterPaperSizeSelector } from "@/features/caja/components/PrinterPaperSizeSelector";
import { TicketFormatoSelector } from "@/features/caja/components/TicketFormatoSelector";
import { ReceiptOptionsSelector } from "@/features/caja/components/ReceiptOptionsSelector";
import { CashDrawerSecuritySettings } from "@/features/caja/components/CashDrawerSecuritySettings";
import { CajaAdminSalesGateSettings } from "@/features/caja/components/CajaAdminSalesGateSettings";
import { CajaChargeAuthSettings } from "@/features/caja/components/CajaChargeAuthSettings";
import { CartRemovalSecuritySettings } from "@/features/caja/components/CartRemovalSecuritySettings";
import { PosSoundSettings } from "@/features/caja/components/PosSoundSettings";
import { PanicContactSettings } from "@/features/caja/components/PanicContactSettings";
import { PaymentMethodsSettings } from "@/features/caja/components/PaymentMethodsSettings";
import { PedidoAutoPrintSettings } from "@/features/tienda/services/tienda.service";
import { NetworkPrinterFields } from "@/features/caja/components/NetworkPrinterFields";
import { BluetoothPrinterFields } from "@/features/caja/components/BluetoothPrinterFields";
import { UsbPrinterFields } from "@/features/caja/components/UsbPrinterFields";
import { LabelPrinterFields } from "@/features/caja/components/LabelPrinterFields";
import { PosHardwareTestsSection } from "@/features/caja/components/PosHardwareTestsSection";
import { PosWebPrintBanner } from "@/features/caja/components/PosWebPrintBanner";
import {
  buildPosBrandOptions,
  buildPosLabelModeOptions,
  buildPosTransportOptions,
  nativeErrorMessage,
  POS_HARDWARE_SECTION_LABEL,
  stableSerializePosPrinter,
} from "@/features/caja/utils/userSettingsPosHardware.helpers";

interface UserSettingsPosHardwareProps {
  /** Dueño o administrador: prueba de impresión y gaveta (no cajeros). */
  canRunHardwareTests?: boolean;
  /**
   * "full" (por defecto): panel completo del slider de perfil.
   * "cashier": solo papel/marca/tipo + pruebas (empleado con acceso a caja).
   */
  mode?: "full" | "cashier";
}

export function UserSettingsPosHardware({
  canRunHardwareTests = false,
  mode = "full",
}: UserSettingsPosHardwareProps) {
  const [cfg, setCfg] = useState<PosPrinterConfig>({
    ...DEFAULT_POS_PRINTER_CONFIG,
  });
  /** Formato del ticket (decisión del negocio; vive en businessConfig, no en la impresora). */
  const [formatoTicket, setFormatoTicket] = useState<TicketFormato>("completo");
  /** IVA/impuesto por defecto para ítems sin tarifa propia (businessConfig). */
  const [impuestoDefaultPct, setImpuestoDefaultPct] = useState(0);
  const [baseline, setBaseline] = useState("");
  const [loading, setLoading] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [btDevices, setBtDevices] = useState<
    { name: string; address: string }[]
  >([]);
  const [btLoading, setBtLoading] = useState(false);
  const [usbDevices, setUsbDevices] = useState<
    {
      name: string;
      vendorId: number;
      productId: number;
      deviceKey: string;
      hasPermission?: boolean;
    }[]
  >([]);
  const [usbLoading, setUsbLoading] = useState(false);
  const [usbPermBusy, setUsbPermBusy] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [labelTestBusy, setLabelTestBusy] = useState(false);
  // Puertos COM disponibles en desktop (Bluetooth emparejado = COM port en Windows)
  const [comPorts, setComPorts] = useState<string[]>([]);
  const [comLoading, setComLoading] = useState(false);
  // Puertos de impresora USB disponibles en desktop (USB001, USB002, etc.)
  const [usbPorts, setUsbPorts] = useState<string[]>([]);
  const [usbPortsLoading, setUsbPortsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const c = await loadPosPrinterConfig();
      if (!cancelled) {
        setCfg(c);
        setBaseline(stableSerializePosPrinter(c));
        setLoading(false);
      }
    })();
    // Formato del ticket: lectura vía service (misma fuente que TicketPreview).
    const biz = businessConfigService.get<Partial<StoredBusinessConfig>>({});
    if (!cancelled) {
      setFormatoTicket(biz.formatoTicket ?? "completo");
      setImpuestoDefaultPct(biz.impuestoDefaultPct ?? 0);
    }
    return () => {
      cancelled = true;
    };
  }, []);

  const dirty = useMemo(
    () => stableSerializePosPrinter(cfg) !== baseline,
    [cfg, baseline],
  );

  const canSave = useMemo(() => {
    if (cfg.transport !== "none" && !isPosPrinterConfigured(cfg)) return false;
    if (cfg.labelPrinterMode === "separate" && !isLabelPrinterConfigured(cfg)) {
      return false;
    }
    return dirty;
  }, [cfg, dirty]);

  /** "full" (dueño/admin) muestra todo; "cashier" solo papel/marca/tipo + pruebas. */
  const isFull = mode === "full";
  const showHardwareTests = canRunHardwareTests || mode === "cashier";

  /** Patch inmutable del config (fusiona sobre el estado actual). */
  const patchCfg = useCallback((patch: Partial<PosPrinterConfig>) => {
    setCfg((p) => ({ ...p, ...patch }));
  }, []);

  /** Patch inmutable de la impresora de etiquetas. */
  const patchLabel = useCallback(
    (patch: Partial<PosPrinterConfig["labelPrinter"]>) => {
      setCfg((p) => ({
        ...p,
        labelPrinter: { ...p.labelPrinter, ...patch },
      }));
    },
    [],
  );

  /** Busca puertos COM disponibles en desktop (Rust → serialport crate). */
  const refreshComPorts = useCallback(async () => {
    setComLoading(true);
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      const ports = await invoke<string[]>("list_serial_ports");
      setComPorts(ports ?? []);
      if (!ports?.length) {
        toast(
          "No se encontraron puertos COM. Asegúrate que la impresora Bluetooth esté emparejada en Ajustes → Bluetooth.",
          {
            duration: 4000,
            id: "com-no-ports",
          },
        );
      }
    } catch (e: unknown) {
      toast.error(nativeErrorMessage(e, "No se pudo leer puertos COM"), {
        id: "com-err",
      });
      setComPorts([]);
    } finally {
      setComLoading(false);
    }
  }, []);

  /** Busca puertos de impresora USB en desktop (Windows: Get-PrinterPort; Linux: /dev/usb/lp*). */
  const refreshUsbPorts = useCallback(async () => {
    setUsbPortsLoading(true);
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      const ports = await invoke<string[]>("list_windows_printer_ports");
      setUsbPorts(ports ?? []);
      if (!ports?.length) {
        toast(
          "No se encontraron puertos de impresora. Asegúrate de que la impresora esté conectada e instalada en Windows (Dispositivos e impresoras).",
          { duration: 5000, id: "usb-no-ports" },
        );
      }
    } catch (e: unknown) {
      toast.error(nativeErrorMessage(e, "No se pudo leer los puertos USB"), {
        id: "usb-ports-err",
      });
      setUsbPorts([]);
    } finally {
      setUsbPortsLoading(false);
    }
  }, []);

  const refreshBluetooth = useCallback(async () => {
    if (!isNative) return;
    setBtLoading(true);
    try {
      const { devices } = await AltenotPos.listPairedBluetoothPrinters();
      setBtDevices(devices ?? []);
      if (!devices?.length) {
        toast(
          "No hay dispositivos emparejados. Empareja la impresora en Ajustes → Bluetooth.",
          { duration: 4000 },
        );
      }
    } catch (e: unknown) {
      toast.error(nativeErrorMessage(e, "No se pudo leer Bluetooth"));
      setBtDevices([]);
    } finally {
      setBtLoading(false);
    }
  }, []);

  const requestUsbAccess = useCallback(
    async (deviceKey?: string): Promise<boolean> => {
      if (isDesktop || !isNative) return true;
      setUsbPermBusy(true);
      try {
        const { granted } = await AltenotPos.requestUsbPermission(
          deviceKey ? { deviceKey } : undefined,
        );
        if (!granted) {
          toast.error(
            "Permiso USB denegado. Pulsa «Conceder permiso» y acepta en el diálogo de Android.",
            { duration: 5000 },
          );
        }
        return granted;
      } catch (e: unknown) {
        toast.error(nativeErrorMessage(e, "No se pudo solicitar permiso USB"));
        return false;
      } finally {
        setUsbPermBusy(false);
      }
    },
    [],
  );

  const refreshUsb = useCallback(async () => {
    if (!isNative) return;
    setUsbLoading(true);
    try {
      const { devices } = await AltenotPos.listUsbPrinters();
      setUsbDevices(devices ?? []);
      if (!devices?.length) {
        toast(
          "No hay impresora USB detectada. Conecta el cable OTG y vuelve a buscar.",
          { duration: 4500 },
        );
      }
    } catch (e: unknown) {
      toast.error(nativeErrorMessage(e, "No se pudo leer USB"));
      setUsbDevices([]);
    } finally {
      setUsbLoading(false);
    }
  }, []);

  const save = async () => {
    await persistPosPrinterConfig(cfg);
    setBaseline(stableSerializePosPrinter(cfg));
    toast.success("Impresora guardada");
  };

  const testPrint = async () => {
    if (!isPosPrinterConfigured(cfg)) {
      toast.error(
        "Completa la configuración de la impresora y guarda antes de probar.",
      );
      return;
    }
    await persistPosPrinterConfig(cfg);
    setBaseline(stableSerializePosPrinter(cfg));
    setTestBusy(true);
    try {
      if (!isNative) {
        // Desktop y Web: construye página ESC/POS → hardwareAdapter → TCP / puerto / WebUSB
        await tryPrintThermalTestPage(cfg.paperWidthMm);
      } else {
        // APK: plugin nativo Capacitor
        await AltenotPos.printTestPage();
        toast.success("Impresión de prueba enviada");
      }
    } catch (e: unknown) {
      toast.error(nativeErrorMessage(e, "Error al imprimir"));
    } finally {
      setTestBusy(false);
    }
  };

  const drawerTestEnabled = isDrawerTestAllowed(cfg);

  const testDrawer = async () => {
    if (!drawerTestEnabled) {
      toast.error(
        cfg.drawerTestUsed
          ? "La prueba de gaveta ya se usó. Por seguridad sólo se permite 1 vez."
          : "Configura y guarda la impresora antes de probar la gaveta.",
        { id: "pos-drawer-locked" },
      );
      return;
    }
    await openCashDrawer({
      context: "movimiento",
      reason: "Prueba desde Preferencias",
    });
    const next = await markDrawerTestUsed(cfg);
    setCfg(next);
    setBaseline(stableSerializePosPrinter(next));
  };

  const confirmReset = async () => {
    setShowResetConfirm(false);
    await resetPosPrinterConfig();
    const fresh = { ...DEFAULT_POS_PRINTER_CONFIG };
    setCfg(fresh);
    setBaseline(stableSerializePosPrinter(fresh));
    setBtDevices([]);
    toast("Configuración de impresora eliminada");
  };

  /** Persiste al instante opciones de recibo para que caja las aplique sin «Guardar». */
  const applyReceiptOptions = (patch: Partial<PosPrinterConfig>) => {
    setCfg((p) => {
      const next = { ...p, ...patch };
      void persistPosPrinterConfig(next).then(() => {
        setBaseline(stableSerializePosPrinter(next));
      });
      return next;
    });
  };

  const setTransport = (t: PosPrinterTransport) => {
    setCfg((p) => {
      const next = { ...p, transport: t };
      if (t === "network" && !p.networkHost.trim()) {
        next.networkHost = POS_DEFAULT_NETWORK_HOST;
      }
      if (t !== "usb") {
        setUsbDevices([]);
      }
      return next;
    });
  };

  /** Imprime una etiqueta de prueba con datos fijos para verificar la 2ª impresora. */
  const testLabel = async () => {
    if (cfg.labelPrinterMode === "off") return;
    if (!isLabelPrinterConfigured(cfg)) {
      toast.error(
        cfg.labelPrinterMode === "separate"
          ? "Configura la impresora de etiquetas (tipo y dirección) antes de probar."
          : "Configura la impresora principal antes de probar la etiqueta.",
        { id: "pos-label-test-cfg" },
      );
      return;
    }
    setLabelTestBusy(true);
    try {
      await persistPosPrinterConfig(cfg);
      setBaseline(stableSerializePosPrinter(cfg));
      const ok = await tryPrintThermalWeighLabel({
        productName: "PRUEBA DE ETIQUETA",
        pricePerKilo: 27500,
        pesoKg: 1.5,
        precioTotal: 41250,
        code128: encodeWeighLabelCode128("PRUEBA", 1.5),
        qrText: encodeWeighLabelQr("PRUEBA", 1.5, 41250),
      });
      if (!ok) {
        toast.error("No se pudo imprimir la etiqueta de prueba", {
          id: "pos-label-test-err",
        });
      }
    } catch (e: unknown) {
      toast.error(
        nativeErrorMessage(e, "Error al imprimir la etiqueta de prueba"),
        { id: "pos-label-test-err" },
      );
    } finally {
      setLabelTestBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="px-4 py-10 flex flex-col items-center gap-2 text-gray-500 dark:text-slate-400">
        <Loader2 className="animate-spin text-primary-600" size={22} />
        <span className="text-xs font-medium">Cargando…</span>
      </div>
    );
  }

  const conn = POS_CONNECTION_TYPE_LABELS;

  const brandOptions = buildPosBrandOptions();
  const transportOptions = buildPosTransportOptions();
  const labelModeOptions = buildPosLabelModeOptions();

  const isWeb = !isNative && !isDesktop;

  /** En web la prueba de impresión solo es posible con relay o WebUSB. */
  const webTestReady =
    !isWeb ||
    (cfg.transport === "network" && cfg.networkRelayUrl.trim().length > 0) ||
    (cfg.transport === "usb" && cfg.usbDeviceKey.trim().length > 0);

  /** Razón por la cual cada transporte necesita configuración extra en web. */
  const webBlockReasons: Record<string, string> = {
    network:
      "El navegador no abre conexiones TCP directas. Pega la URL del relay local (Wi-Fi) en «Dirección de red» para imprimir desde web.",
    bluetooth_spp:
      "Bluetooth SPP no es soportado por los navegadores. Usa la APK o app de escritorio.",
    usb: "En el navegador usa WebUSB (Chrome/Edge con HTTPS); el driver de Windows puede bloquear el acceso.",
  };

  return (
    <div className="px-3 pt-4 pb-4 space-y-5 text-left">
      {/* 1. Nombre de la impresora — primero, etiqueta flotante reutilizable */}
      <FloatingLabelInput
        label="Nombre de la impresora"
        hint="Ej. Caja 1, Mostrador, Cocina"
        icon={<Printer size={14} />}
        value={cfg.deviceName}
        onChange={(e) => patchCfg({ deviceName: e.target.value })}
      />

      {/* 2. Tamaño de papel — comparativa visual */}
      <div>
        <p className={POS_HARDWARE_SECTION_LABEL}>Tamaño de papel</p>
        <PrinterPaperSizeSelector
          value={cfg.paperWidthMm}
          onChange={(mm) => patchCfg({ paperWidthMm: mm })}
        />
      </div>

      {/* 2b. Formato del ticket térmico — decisión del negocio (dueño/admin) */}
      {isFull && (
        <div>
          <p className={POS_HARDWARE_SECTION_LABEL}>
            Formato del ticket térmico
          </p>
          <TicketFormatoSelector
            value={formatoTicket}
            onChange={(formato) => {
              setFormatoTicket(formato);
              businessConfigService.update((current) => ({
                ...current,
                formatoTicket: formato,
              }));
              toast.success(
                formato === "compacto" || formato === "ultracompacto"
                  ? "Ticket compacto activado: ahorras papel"
                  : "Ticket completo activado",
                { id: "ticket-formato" },
              );
            }}
          />
          <p className="mt-1.5 text-[10px] text-gray-500 dark:text-slate-400 leading-relaxed px-0.5">
            El formato aplica a la impresión térmica del ticket de venta y se
            guarda en los datos del negocio (mismo lugar que en la vista previa
            del ticket y en «Configurar Datos del Negocio»). Los formatos
            «Compacto» y «Ultra» se usan en ventas estándar; si un comprobante
            lleva devoluciones, anulaciones, crédito o puntos, se imprime
            completo automáticamente para no perder información.
          </p>
        </div>
      )}

      {/* 3. Marca y tipo de impresora — MenuSelect (no <select> nativo) */}
      <div className="space-y-3">
        <div>
          <p className={POS_HARDWARE_SECTION_LABEL}>Marca</p>
          <div className="relative">
            <span className="abs-center-y left-3 z-[1] pointer-events-none text-primary-600">
              <Cpu size={14} />
            </span>
            <MenuSelect
              value={cfg.brand}
              options={brandOptions}
              onChange={(v) => patchCfg({ brand: v })}
              className="[&>button]:pl-9"
            />
          </div>
        </div>
        <div>
          <p className={POS_HARDWARE_SECTION_LABEL}>Tipo de impresora</p>
          <div className="relative">
            <span className="abs-center-y left-3 z-[1] pointer-events-none text-primary-600">
              <Printer size={14} />
            </span>
            <MenuSelect
              value={cfg.transport}
              options={transportOptions}
              onChange={(v) => setTransport(v as PosPrinterTransport)}
              className="[&>button]:pl-9"
            />
          </div>
          {cfg.transport !== "none" && (
            <p className="mt-1 text-[10px] text-gray-500 dark:text-slate-400 leading-relaxed px-0.5">
              {cfg.transport === "network" && conn.network.description}
              {cfg.transport === "bluetooth_spp" &&
                conn.bluetooth_spp.description}
              {cfg.transport === "usb" && conn.usb.description}
            </p>
          )}
          {isWeb &&
            cfg.transport !== "none" &&
            !(cfg.transport === "network" && cfg.networkRelayUrl.trim()) && (
              <div className="mt-1.5 flex items-start gap-1.5 px-1">
                <AlertTriangle
                  size={11}
                  className="text-amber-500 dark:text-amber-400 shrink-0 mt-px"
                />
                <p className="text-[10px] text-amber-700 dark:text-amber-300 leading-snug font-medium">
                  {webBlockReasons[cfg.transport]}
                </p>
              </div>
            )}
        </div>
      </div>

      {/* Wi‑Fi: aviso simple para no técnicos */}
      {cfg.transport === "network" && (
        <div className="rounded-xl border border-primary-200 dark:border-primary-800/60 bg-primary-50/80 dark:bg-primary-950/25 p-3">
          <div className="flex items-start gap-2">
            <Wifi
              size={16}
              className="text-primary-600 dark:text-primary-400 shrink-0 mt-0.5"
            />
            <div className="text-[11px] text-primary-900 dark:text-primary-100 leading-snug font-medium">
              <p className="font-bold mb-1">Conéctala al mismo Wi-Fi</p>
              <p>
                La impresora y este dispositivo deben estar en el{" "}
                <strong>mismo Wi-Fi del negocio</strong>. Si no sabes la IP de
                tu impresora, imprime un ticket de prueba desde su botón físico
                o búscala en la app oficial de la marca: ahí aparece.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Red */}
      {cfg.transport === "network" && (
        <NetworkPrinterFields cfg={cfg} onChange={patchCfg} isWeb={isWeb} />
      )}

      {/* Bluetooth */}
      {cfg.transport === "bluetooth_spp" && (
        <BluetoothPrinterFields
          cfg={cfg}
          onChange={patchCfg}
          isDesktop={isDesktop}
          comPorts={comPorts}
          comLoading={comLoading}
          onRefreshComPorts={() => void refreshComPorts()}
          btDevices={btDevices}
          btLoading={btLoading}
          onRefreshBluetooth={() => void refreshBluetooth()}
        />
      )}

      {/* USB */}
      {cfg.transport === "usb" && (
        <UsbPrinterFields
          cfg={cfg}
          onChange={patchCfg}
          isDesktop={isDesktop}
          isNative={isNative}
          usbPorts={usbPorts}
          usbPortsLoading={usbPortsLoading}
          onRefreshUsbPorts={() => void refreshUsbPorts()}
          usbDevices={usbDevices}
          usbLoading={usbLoading}
          usbPermBusy={usbPermBusy}
          onRefreshUsb={() => void refreshUsb()}
          onRequestUsbAccess={requestUsbAccess}
        />
      )}

      {/* 4. Impresora de etiquetas de pesaje (carnicería / venta por peso) */}
      <LabelPrinterFields
        cfg={cfg}
        onLabelModeChange={(m: LabelPrinterMode) =>
          patchCfg({ labelPrinterMode: m })
        }
        onLabelChange={patchLabel}
        isNative={isNative}
        isDesktop={isDesktop}
        transportOptions={transportOptions}
        labelModeOptions={labelModeOptions}
        labelTestBusy={labelTestBusy}
        labelPrinterConfigured={isLabelPrinterConfigured(cfg)}
        onTestLabel={() => void testLabel()}
      />

      {/* 5. Pruebas de hardware — dueño/admin (modo full) y cajero (modo cashier) */}
      {showHardwareTests ? (
        <PosHardwareTestsSection
          testBusy={testBusy}
          onTestPrint={() => void testPrint()}
          drawerTestEnabled={drawerTestEnabled}
          drawerTestUsed={cfg.drawerTestUsed}
          onTestDrawer={() => void testDrawer()}
          isWeb={isWeb}
          webTestReady={webTestReady}
          printerConfigured={isPosPrinterConfigured(cfg)}
          transportNone={cfg.transport === "none"}
        />
      ) : null}

      {/* 6. Opciones avanzadas — al final para no separar pruebas de la config básica */}
      {isFull && (
        <>
          <button
            type="button"
            onClick={() => setAdvancedOpen((v) => !v)}
            aria-expanded={advancedOpen}
            className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50/80 dark:bg-slate-800/60 text-xs font-bold text-gray-700 dark:text-slate-200"
          >
            Opciones avanzadas
            <ChevronDown
              size={16}
              className={`transition-transform ${advancedOpen ? "rotate-180" : ""}`}
            />
          </button>
          {advancedOpen && (
            <div className="space-y-2.5">
              <div>
                <p className={POS_HARDWARE_SECTION_LABEL}>
                  Métodos de pago aceptados
                </p>
                <PaymentMethodsSettings />
              </div>
              <div>
                <p className={POS_HARDWARE_SECTION_LABEL}>
                  Control por efectivo estimado
                </p>
                <CashDrawerSecuritySettings />
              </div>
              <CajaChargeAuthSettings />
              <CajaAdminSalesGateSettings />
              <div>
                <p className={POS_HARDWARE_SECTION_LABEL}>
                  Seguridad de eliminación de productos
                </p>
                <CartRemovalSecuritySettings />
              </div>
              <div>
                <p className={POS_HARDWARE_SECTION_LABEL}>
                  Sonidos y avisos del POS
                </p>
                <PosSoundSettings />
              </div>
              <PanicContactSettings />
              <div>
                <p className={POS_HARDWARE_SECTION_LABEL}>
                  Impuestos en Caja · IVA por defecto
                </p>
                <div className="relative">
                  <span className="abs-center-y left-3 z-[1] pointer-events-none text-primary-600">
                    <Percent size={14} />
                  </span>
                  <MenuSelect
                    value={String(impuestoDefaultPct)}
                    options={[
                      { value: "0", label: "Sin tarifa por defecto" },
                      { value: "19", label: "IVA 19%" },
                      { value: "8", label: "Impoconsumo 8%" },
                      { value: "5", label: "IVA 5%" },
                    ]}
                    onChange={(v) => {
                      const pct = Number(v) || 0;
                      setImpuestoDefaultPct(pct);
                      businessConfigService.update((current) => ({
                        ...current,
                        impuestoDefaultPct: pct,
                      }));
                      toast.success("Impuestos en Caja guardados", {
                        id: "pos-impuesto-default",
                      });
                    }}
                    className="[&>button]:pl-9"
                  />
                </div>
                <p className="mt-1.5 text-[10px] text-gray-500 dark:text-slate-400 leading-relaxed px-0.5">
                  Tarifa por defecto para ítems sin impuesto asignado en su
                  producto. El PVP ya incluye IVA: solo informa la base gravable
                  y el impuesto en el ticket y en la factura DIAN. 0 = sin
                  tarifa por defecto.
                </p>
              </div>
              <ReceiptOptionsSelector
                cfg={cfg}
                onChange={applyReceiptOptions}
              />
              <div className="rounded-xl border border-dashed border-gray-200 dark:border-slate-600 p-3 text-[10px] text-gray-500 dark:text-slate-400 space-y-1">
                <p>
                  La gaveta se abre solo al cobrar en efectivo (para entregar
                  cambio). Comando ESC/POS estándar; puerto RAW habitual:{" "}
                  <strong>9100</strong>.
                </p>
              </div>
              <PedidoAutoPrintSettings />
            </div>
          )}
        </>
      )}

      {/* Banner web al final: vías disponibles para imprimir desde el navegador */}
      {isWeb && <PosWebPrintBanner />}

      {/* Pie acciones */}
      <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-slate-700">
        {isFull && (
          <button
            type="button"
            onClick={() => setShowResetConfirm(true)}
            className="shrink-0 w-11 h-11 rounded-full bg-gray-200 dark:bg-slate-700 flex items-center justify-center text-gray-600 dark:text-slate-300"
            aria-label="Eliminar configuración"
          >
            <Trash2 size={18} />
          </button>
        )}
        <button
          type="button"
          onClick={() => void save()}
          disabled={!canSave}
          className="flex-1 py-3 rounded-xl bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 dark:disabled:bg-slate-600 text-white font-black text-xs uppercase tracking-widest disabled:cursor-not-allowed"
        >
          Guardar
        </button>
      </div>

      <ConfirmationModal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={() => void confirmReset()}
        title="¿Quitar impresora?"
        message="Se borrará la configuración de esta térmica en este dispositivo y se liberará la prueba de gaveta."
        variant="danger"
        confirmText="Eliminar"
        cancelText="Cancelar"
      />
    </div>
  );
}
